import React, { useState, useEffect, useMemo, ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useSubscription } from "@/hooks/useSubscription";
import { useChurch } from "@/hooks/useChurch";
import { usePermissions } from "@/hooks/usePermissions";
import { useFreemium } from "@/hooks/useFreemium";
import { AlertTriangle, Check, X, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn, sortGeneralFirst } from "@/lib/utils";
import type { Tables } from "@/integrations/supabase/types";

type Member = Tables<"members">;


const EDUCATION_STATUSES = ["Not in School", "Preschool", "Elementary School", "Middle School", "High School", "College / University", "Vocational / Trade School", "Completed / Graduated"];
const YEAR_LEVELS_BY_STATUS: Record<string, string[]> = {
  "Preschool": ["Pre-K", "Kindergarten"],
  "Elementary School": ["Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5"],
  "Middle School": ["Grade 6", "Grade 7", "Grade 8"],
  "High School": ["Grade 9", "Grade 10", "Grade 11", "Grade 12"],
  "College / University": ["Freshman", "Sophomore", "Junior", "Senior", "Master's", "PhD"],
  "Vocational / Trade School": ["1st Year", "2nd Year", "3rd Year"],
};
const DEGREE_TYPES = ["High School Diploma", "Associate", "Bachelor's", "Master's", "Doctorate", "Trade Certificate"];
const EMPLOYMENT_STATUSES = ["Employed Full-Time", "Employed Part-Time", "Self-Employed", "Unemployed", "Retired"];
const INDUSTRIES = [
"Healthcare", "Education", "Business", "Technology", "Construction", "Retail", "Manufacturing", "Other"];
const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware",
  "Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky",
  "Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi",
  "Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico",
  "New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania",
  "Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont",
  "Virginia","Washington","West Virginia","Wisconsin","Wyoming",
];

/** Auto-format phone: 000-000-0000 */
function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function parseDepartmentValues(value: string | null | undefined): string[] {
  if (!value) return [];
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizeMinistrySelection(values: string[]): string[] {
  const unique = Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));
  if (!unique.some((value) => value.toLowerCase().startsWith("general"))) {
    unique.unshift("General");
  }
  return sortGeneralFirst(unique);
}

interface Props { open: boolean; onClose: () => void; member: Member | null; }

class MemberFormErrorBoundary extends React.Component<
  { children: ReactNode; onClose: () => void },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode; onClose: () => void }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    console.error("Member form crashed:", error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 space-y-3">
          <p className="text-sm font-medium text-foreground">Member form failed to load.</p>
          <p className="text-xs text-muted-foreground">Please close and reopen the form. If this continues, refresh the page.</p>
          <Button type="button" variant="outline" size="sm" onClick={this.props.onClose}>Close</Button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function MemberFormDialog({ open, onClose, member }: Props) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { isAtMemberLimit, memberCount, memberLimit } = useSubscription();
  const { requireSubscription } = useFreemium();
  const { profile } = useChurch();
  const { can, isChurchAdmin } = usePermissions();
  const isAdmin = isChurchAdmin || can("can_edit_members") || can("can_add_members");
  const isEdit = !!member;

  const [form, setForm] = useState({
    first_name: "", middle_name: "", last_name: "",
    gender: "",
    email: "", phone: "", address: "", city: "", state: "", zip_code: "",
    date_of_birth: "", date_joined: "", family_household: "", department: "General" as string,
    baptism_status: false, notes: "", status: "Active",
    marital_status: "",
    education_status: "", school_name: "", year_level: "", field_of_study: "", degree_earned: "",
    employment_status: "", occupation: "", industry: "", employer_name: "",
  });
  const { data: families = [] } = useQuery({
    queryKey: ["families_list"],
    queryFn: async () => {
      const { data, error } = await supabase.from("families").select("id, family_name, address, city, state, zip_code").order("family_name");
      if (error) throw error;
      return data;
    },
  });

  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations_full"],
    queryFn: async () => {
      const { data, error } = await supabase.from("organizations").select("id, name, type, level, parent_id").order("type").order("name");
      if (error) throw error;
      return data;
    },
  });

  // Cascading org selection state
  const [selectedMinistries, setSelectedMinistries] = useState<string[]>(["General"]);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [selectedSubId, setSelectedSubId] = useState("");

  const ministryOptions = useMemo(
    () =>
      normalizeMinistrySelection(
        organizations
          .filter((o: any) => (o.type || "").toLowerCase() === "ministry")
          .map((o: any) => o.name)
          .filter((name: string) => !!name)
      ),
    [organizations]
  );
  // On edit, map saved department back to selected ministries.
  useEffect(() => {
    setSelectedMinistries(normalizeMinistrySelection(parseDepartmentValues(member?.department)));
  }, [member]);

  useEffect(() => {
    const normalized = normalizeMinistrySelection(selectedMinistries);
    const current = form.department || "";
    const next = normalized.join(", ");

    if (normalized.join("|") !== selectedMinistries.join("|")) {
      setSelectedMinistries(normalized);
      return;
    }

    if (current !== next) {
      update("department", next);
    }
  }, [selectedMinistries]);

  useEffect(() => {
    if (member) {
      const m = member as any;
      setForm({
        first_name: m.first_name || member.full_name?.split(" ")[0] || "",
        middle_name: m.middle_name || "",
        last_name: m.last_name || "",
        gender: m.gender || "",
        email: member.email || "", phone: member.phone || "",
        address: member.address || "",
        city: m.city || "", state: m.state || "", zip_code: m.zip_code || "",
        date_of_birth: m.date_of_birth || "",
        date_joined: member.date_joined || "",
        family_household: member.family_household || "",
        department: member.department || "",
        baptism_status: member.baptism_status ?? false,
        notes: member.notes || "", status: member.status || "Active",
        education_status: m.education_status || "",
        school_name: m.school_name || "",
        year_level: m.year_level || "",
        field_of_study: m.field_of_study || "",
        degree_earned: m.degree_earned || "",
        employment_status: m.employment_status || "",
        occupation: m.occupation || "",
        industry: m.industry || "",
        employer_name: m.employer_name || "",
        marital_status: (m as any).marital_status || "",
      });
      if (m.date_of_birth) {
        const [y, mo, d] = m.date_of_birth.split("-");
        setDobYear(y || ""); setDobMonth(String(parseInt(mo || "0", 10))); setDobDay(String(parseInt(d || "0", 10)));
      } else {
        setDobMonth(""); setDobDay(""); setDobYear("");
      }
      if (member.date_joined) {
        const [jy, jmo, jd] = member.date_joined.split("-");
        setDjYear(jy || ""); setDjMonth(String(parseInt(jmo || "0", 10))); setDjDay(String(parseInt(jd || "0", 10)));
      } else {
        setDjMonth(""); setDjDay(""); setDjYear("");
      }
    } else {
      setForm({
        first_name: "", middle_name: "", last_name: "",
        gender: "",
        email: "", phone: "", address: "", city: "", state: "", zip_code: "",
        date_of_birth: "", date_joined: "", family_household: "", department: "General",
        baptism_status: false, notes: "", status: "Active",
        marital_status: "",
        education_status: "", school_name: "", year_level: "", field_of_study: "", degree_earned: "",
        employment_status: "", occupation: "", industry: "", employer_name: "",
      });
      setSelectedMinistries(["General"]);
      setSelectedOrgId("");
      setSelectedSubId("");
      setAddressFromFamily(false);
      setDobMonth(""); setDobDay(""); setDobYear("");
      setDjMonth(""); setDjDay(""); setDjYear("");
    }
  }, [member, open]);

  // Family autocomplete state
  const [familyQuery, setFamilyQuery] = useState("");
  const [familyOpen, setFamilyOpen] = useState(false);
  const [addressFromFamily, setAddressFromFamily] = useState(false);

  // City / State autocomplete state
  const [cityOpen, setCityOpen] = useState(false);
  const [stateOpen, setStateOpen] = useState(false);

  const { data: memberCities = [] } = useQuery({
    queryKey: ["member_cities", profile?.id],
    queryFn: async () => {
      const { data } = await supabase.from("members").select("city").eq("church_id", profile!.id);
      return [...new Set((data || []).map((r) => r.city).filter(Boolean))] as string[];
    },
    enabled: !!profile?.id,
  });

  const filteredCities = useMemo(() => {
    const q = form.city.toLowerCase();
    if (!q) return [];
    return memberCities.filter((c) => c.toLowerCase().includes(q)).slice(0, 8);
  }, [form.city, memberCities]);

  const filteredStates = useMemo(() => {
    const q = form.state.toLowerCase();
    if (!q) return US_STATES.slice(0, 10);
    return US_STATES.filter((s) => s.toLowerCase().includes(q)).slice(0, 10);
  }, [form.state]);

  // DOB part state (Month / Day / Year selects)
  const [dobMonth, setDobMonth] = useState("");
  const [dobDay, setDobDay] = useState("");
  const [dobYear, setDobYear] = useState("");

  // Date Joined part state
  const [djMonth, setDjMonth] = useState("");
  const [djDay, setDjDay] = useState("");
  const [djYear, setDjYear] = useState("");

  const DOB_MONTHS = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December",
  ];
  const currentYear = new Date().getFullYear();
  const DOB_YEARS = Array.from({ length: currentYear - 1900 + 1 }, (_, i) => String(currentYear - i));
  const dobDaysInMonth = dobMonth && dobYear
    ? new Date(Number(dobYear), Number(dobMonth), 0).getDate()
    : dobMonth ? new Date(2000, Number(dobMonth), 0).getDate() : 31;
  const DOB_DAYS = Array.from({ length: dobDaysInMonth }, (_, i) => String(i + 1));

  const applyDobParts = (month: string, day: string, year: string) => {
    if (month && day && year) {
      const iso = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
      update("date_of_birth", iso);
      inferEducationFromAge(iso);
    } else {
      update("date_of_birth", "");
    }
  };

  const djDaysInMonth = djMonth && djYear
    ? new Date(Number(djYear), Number(djMonth), 0).getDate()
    : djMonth ? new Date(2000, Number(djMonth), 0).getDate() : 31;
  const DJ_DAYS = Array.from({ length: djDaysInMonth }, (_, i) => String(i + 1));

  const applyDjParts = (month: string, day: string, year: string) => {
    if (month && day && year) {
      update("date_joined", `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`);
    } else {
      update("date_joined", "");
    }
  };

  useEffect(() => {
    setFamilyQuery(form.family_household);
  }, [form.family_household]);

  const filteredFamilies = familyQuery.trim()
    ? families.filter((f) => f.family_name.toLowerCase().includes(familyQuery.toLowerCase())).slice(0, 8)
    : families.slice(0, 8);

  const mutation = useMutation({
    mutationFn: async () => {
      const fullName = [form.first_name.trim(), form.middle_name.trim(), form.last_name.trim()].filter(Boolean).join(" ");
      const payload: any = {
        full_name: fullName,
        first_name: form.first_name.trim() || null,
        middle_name: form.middle_name.trim() || null,
        last_name: form.last_name.trim() || null,
        gender: form.gender || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        address: form.address.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        zip_code: form.zip_code.trim() || null,
        date_of_birth: form.date_of_birth || null,
        date_joined: form.date_joined || null,
        family_household: form.family_household || null,
        department: form.department || null,
        baptism_status: form.baptism_status,
        notes: form.notes.trim() || null,
        status: form.status,
        marital_status: (form as any).marital_status || null,
        education_status: form.education_status || null,
        school_name: form.school_name.trim() || null,
        year_level: form.year_level || null,
        field_of_study: form.field_of_study.trim() || null,
        degree_earned: form.degree_earned || null,
        employment_status: form.employment_status || (memberAge !== null && memberAge > 18 ? "Unemployed" : null),
        occupation: form.occupation.trim() || null,
        industry: form.industry || null,
        employer_name: form.employer_name.trim() || null,
      };
      let memberId = member?.id;
      if (isEdit) {
        const { error } = await supabase.from("members").update(payload).eq("id", member.id);
        if (error) throw error;
      } else {
        payload.church_id = profile?.id;
        const { data: inserted, error } = await supabase.from("members").insert(payload).select("id").single();
        if (error) throw error;
        memberId = inserted.id;
      }

    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["members"] });
      queryClient.invalidateQueries({ queryKey: ["member_count"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard_all_members"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard_member_count"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard_families"] });
      queryClient.invalidateQueries({ queryKey: ["organization_leaders"] });
      
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!requireSubscription()) return;
    if (!form.first_name.trim()) { toast.error("First name is required"); return; }
    if (!form.last_name.trim()) { toast.error("Last name is required"); return; }
    if (!isEdit && isAtMemberLimit) { toast.error("Member limit reached. Upgrade your plan."); return; }
    mutation.mutate();
  };

  const update = (key: string, value: string | boolean) => setForm((prev) => ({ ...prev, [key]: value }));

  const inferEducationFromAge = (dob: string) => {
    if (!dob) return;
    const today = new Date();
    const birth = new Date(dob + "T00:00:00");
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    if (age < 0) return;
    let status = "";
    let yearLevel = "";
    if (age <= 2) { status = "Not in School"; yearLevel = ""; }
    else if (age === 3) { status = "Preschool"; yearLevel = "Pre-K"; }
    else if (age === 4) { status = "Preschool"; yearLevel = "Kindergarten"; }
    else if (age === 5) { status = "Elementary School"; yearLevel = "Grade 1"; }
    else if (age === 6) { status = "Elementary School"; yearLevel = "Grade 1"; }
    else if (age === 7) { status = "Elementary School"; yearLevel = "Grade 2"; }
    else if (age === 8) { status = "Elementary School"; yearLevel = "Grade 3"; }
    else if (age === 9) { status = "Elementary School"; yearLevel = "Grade 4"; }
    else if (age === 10) { status = "Elementary School"; yearLevel = "Grade 5"; }
    else if (age === 11) { status = "Middle School"; yearLevel = "Grade 6"; }
    else if (age === 12) { status = "Middle School"; yearLevel = "Grade 7"; }
    else if (age === 13) { status = "Middle School"; yearLevel = "Grade 8"; }
    else if (age === 14) { status = "High School"; yearLevel = "Grade 9"; }
    else if (age === 15) { status = "High School"; yearLevel = "Grade 10"; }
    else if (age === 16) { status = "High School"; yearLevel = "Grade 11"; }
    else if (age <= 18) { status = "High School"; yearLevel = "Grade 12"; }
    else { status = "College / University"; yearLevel = ""; }
    setForm((prev) => ({ ...prev, education_status: status, year_level: yearLevel }));
  };
  const showLimitWarning = !isEdit && isAtMemberLimit;

  const showEducationDetails = ["Preschool", "Elementary School", "Middle School", "High School", "College / University", "Vocational / Trade School"].includes(form.education_status);
  const showGraduatedDetails = form.education_status === "Completed / Graduated";
  const showEmploymentDetails = ["Employed Full-Time", "Employed Part-Time", "Self-Employed"].includes(form.employment_status);
  const memberAge = useMemo(() => {
    if (!form.date_of_birth) return null;
    const today = new Date();
    const birth = new Date(form.date_of_birth + "T00:00:00");
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  }, [form.date_of_birth]);
  const showEmploymentSection = memberAge === null || memberAge >= 14;
  const fieldClassName = "h-12 text-base sm:h-10 sm:text-sm";
  const selectTriggerClassName = "h-12 text-base sm:h-10 sm:text-sm";
  const textAreaClassName = "min-h-[120px] text-base sm:min-h-[80px] sm:text-sm";

  const personalInformationSection = (
    <div>
      {!isMobile && <h4 className="text-sm font-semibold text-foreground mb-2">Personal Information</h4>}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
        <div className="space-y-2 md:col-span-2">
          <Label>First Name *</Label>
          <Input className={fieldClassName} value={form.first_name} onChange={(e) => update("first_name", e.target.value)} required />
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label>Middle Name</Label>
          <Input className={fieldClassName} value={form.middle_name} onChange={(e) => update("middle_name", e.target.value)} />
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label>Last Name *</Label>
          <Input className={fieldClassName} value={form.last_name} onChange={(e) => update("last_name", e.target.value)} required />
        </div>
        <div className="space-y-2 md:col-span-3">
          <Label>Gender</Label>
          <Select value={form.gender} onValueChange={(v) => update("gender", v === "_none" ? "" : v)}>
            <SelectTrigger className={selectTriggerClassName}><SelectValue placeholder="Select gender" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">None</SelectItem>
              <SelectItem value="Male">Male</SelectItem>
              <SelectItem value="Female">Female</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 md:col-span-3">
          <Label>Date of Birth</Label>
          {isMobile ? (
            <Input
              className={fieldClassName}
              type="date"
              value={form.date_of_birth}
              onChange={(e) => {
                update("date_of_birth", e.target.value);
                if (e.target.value) inferEducationFromAge(e.target.value);
              }}
            />
          ) : (
            <div className="grid grid-cols-3 gap-1">
              <Select value={dobMonth} onValueChange={(v) => { const m = v === "_none" ? "" : v; setDobMonth(m); applyDobParts(m, dobDay, dobYear); }}>
                <SelectTrigger className="text-sm"><SelectValue placeholder="Month" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Month</SelectItem>
                  {DOB_MONTHS.map((name, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={dobDay} onValueChange={(v) => { const d = v === "_none" ? "" : v; setDobDay(d); applyDobParts(dobMonth, d, dobYear); }}>
                <SelectTrigger className="text-sm"><SelectValue placeholder="Day" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Day</SelectItem>
                  {DOB_DAYS.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={dobYear} onValueChange={(v) => { const y = v === "_none" ? "" : v; setDobYear(y); applyDobParts(dobMonth, dobDay, y); }}>
                <SelectTrigger className="text-sm"><SelectValue placeholder="Year" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Year</SelectItem>
                  {DOB_YEARS.map((y) => (
                    <SelectItem key={y} value={y}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="space-y-2 md:col-span-3">
          <Label>Phone</Label>
          <Input
            className={fieldClassName}
            value={form.phone}
            onChange={(e) => update("phone", formatPhone(e.target.value))}
            placeholder="000-000-0000"
            maxLength={12}
          />
        </div>
        <div className="space-y-2 md:col-span-3">
          <Label>Email</Label>
          <Input className={fieldClassName} type="email" value={form.email} onChange={(e) => update("email", e.target.value)} />
        </div>
      </div>
    </div>
  );

  const familySection = (
    <div>
      {!isMobile && <h4 className="text-sm font-semibold text-foreground mb-2">Family Information</h4>}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="space-y-1">
          <Label>Marital Status</Label>
          <Select value={(form as any).marital_status} onValueChange={(v) => update("marital_status", v === "_none" ? "" : v)}>
            <SelectTrigger className={selectTriggerClassName}><SelectValue placeholder="Select status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">None</SelectItem>
              <SelectItem value="Single">Single</SelectItem>
              <SelectItem value="Married">Married</SelectItem>
              <SelectItem value="Widowed">Widowed</SelectItem>
              <SelectItem value="Divorced">Divorced</SelectItem>
              <SelectItem value="Separated">Separated</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Family / Household</Label>
          <div className="space-y-2">
            <div className="relative">
              <Input
                className={fieldClassName}
                value={familyQuery}
                onChange={(e) => {
                  setFamilyQuery(e.target.value);
                  update("family_household", e.target.value);
                  setFamilyOpen(true);
                }}
                onFocus={() => setFamilyOpen(true)}
                onBlur={() => setTimeout(() => setFamilyOpen(false), 200)}
                placeholder="Search family..."
                autoComplete="off"
              />
              {familyOpen && filteredFamilies.length > 0 && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-md max-h-56 overflow-y-auto">
                  {filteredFamilies.map((f) => {
                    const addr = [(f as any).address, (f as any).city, (f as any).state].filter(Boolean).join(", ");
                    return (
                    <button
                      key={f.id}
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-accent/10 transition-colors flex items-center gap-2"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        update("family_household", f.family_name);
                        setFamilyQuery(f.family_name);
                        setFamilyOpen(false);
                        if ((f as any).address || (f as any).city || (f as any).state || (f as any).zip_code) {
                          setForm(prev => ({
                            ...prev,
                            address: (f as any).address || "",
                            city: (f as any).city || "",
                            state: (f as any).state || "",
                            zip_code: (f as any).zip_code || "",
                          }));
                          setAddressFromFamily(true);
                        }
                      }}
                    >
                      <Users className="w-4 h-4 text-muted-foreground shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{f.family_name}</div>
                        {addr && <div className="text-xs text-muted-foreground truncate">{addr}</div>}
                      </div>
                    </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const addressSection = (
    <div>
      <div className="flex items-center justify-between mb-2">
        {!isMobile && <h4 className="text-sm font-semibold text-foreground">Address</h4>}
        {addressFromFamily && (
          <button
            type="button"
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive transition-colors"
            onClick={() => {
              setForm(prev => ({ ...prev, address: "", city: "", state: "", zip_code: "" }));
              setAddressFromFamily(false);
            }}
          >
            <X className="w-3 h-3" />
            Pre-filled from family · Clear
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
        <div className="space-y-2 md:col-span-6">
          <Label>Street Address</Label>
          <Input className={fieldClassName} value={form.address} onChange={(e) => update("address", e.target.value)} />
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label>City</Label>
          <div className="relative">
            <Input
              className={fieldClassName}
              value={form.city}
              onChange={(e) => { update("city", e.target.value); setCityOpen(true); }}
              onFocus={() => setCityOpen(true)}
              onBlur={() => setTimeout(() => setCityOpen(false), 150)}
              autoComplete="off"
            />
            {cityOpen && filteredCities.length > 0 && (
              <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-md max-h-48 overflow-y-auto">
                {filteredCities.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent/10 transition-colors"
                    onMouseDown={(e) => { e.preventDefault(); update("city", c); setCityOpen(false); }}
                  >{c}</button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label>State</Label>
          <div className="relative">
            <Input
              className={fieldClassName}
              value={form.state}
              onChange={(e) => { update("state", e.target.value); setStateOpen(true); }}
              onFocus={() => setStateOpen(true)}
              onBlur={() => setTimeout(() => setStateOpen(false), 150)}
              autoComplete="off"
              placeholder="Type to search state"
            />
            {stateOpen && filteredStates.length > 0 && (
              <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-md max-h-48 overflow-y-auto">
                {filteredStates.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent/10 transition-colors"
                    onMouseDown={(e) => { e.preventDefault(); update("state", s); setStateOpen(false); }}
                  >{s}</button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label>Zip Code</Label>
          <Input className={fieldClassName} value={form.zip_code} onChange={(e) => update("zip_code", e.target.value)} />
        </div>
      </div>
    </div>
  );

  const churchInformationSection = (
    <div>
      {!isMobile && <h4 className="text-sm font-semibold text-foreground mb-2">Church Information</h4>}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
        <div className="space-y-2 md:col-span-3">
          <Label>Date Joined</Label>
          {isMobile ? (
            <Input
              className={fieldClassName}
              type="date"
              value={form.date_joined}
              onChange={(e) => update("date_joined", e.target.value)}
            />
          ) : (
            <div className="grid grid-cols-3 gap-1">
              <Select value={djMonth} onValueChange={(v) => { const m = v === "_none" ? "" : v; setDjMonth(m); applyDjParts(m, djDay, djYear); }}>
                <SelectTrigger className="text-sm"><SelectValue placeholder="Month" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Month</SelectItem>
                  {DOB_MONTHS.map((name, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={djDay} onValueChange={(v) => { const d = v === "_none" ? "" : v; setDjDay(d); applyDjParts(djMonth, d, djYear); }}>
                <SelectTrigger className="text-sm"><SelectValue placeholder="Day" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Day</SelectItem>
                  {DJ_DAYS.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={djYear} onValueChange={(v) => { const y = v === "_none" ? "" : v; setDjYear(y); applyDjParts(djMonth, djDay, y); }}>
                <SelectTrigger className="text-sm"><SelectValue placeholder="Year" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Year</SelectItem>
                  {DOB_YEARS.map((y) => (
                    <SelectItem key={y} value={y}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="space-y-2 md:col-span-3">
          <Label>Ministry Type</Label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" className={cn("w-full justify-between font-normal", isMobile ? "h-12 text-base" : "") }>
                <span className="truncate">
                  {selectedMinistries.join(", ")}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)] max-h-64 overflow-y-auto p-2">
              <p className="mb-1 px-1 text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider">Ministry</p>
              <div className="space-y-0.5">
                {ministryOptions.map((name) => {
                  const isChecked = selectedMinistries.includes(name);
                  const isGeneral = name.toLowerCase().startsWith("general");

                  return (
                    <DropdownMenuItem
                      key={name}
                      onSelect={(e) => {
                        e.preventDefault();
                        setSelectedOrgId("");
                        setSelectedSubId("");

                        if (isGeneral) return;

                        const next = isChecked
                          ? selectedMinistries.filter((value) => value !== name)
                          : [...selectedMinistries, name];
                        setSelectedMinistries(normalizeMinistrySelection(next));
                      }}
                      className="w-full flex items-center gap-2.5 rounded-md px-2 py-1.5"
                    >
                      <span
                        className={cn(
                          "inline-flex h-4 w-4 items-center justify-center shrink-0 rounded border-[1.5px]",
                          isChecked ? "bg-[#1e3a8a] border-[#1e3a8a]" : "bg-white border-slate-300"
                        )}
                      >
                        {isChecked && <Check className="h-2.5 w-2.5 text-white" />}
                      </span>
                      <span className="text-sm text-foreground">{name}</span>
                    </DropdownMenuItem>
                  );
                })}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label>Status</Label>
          <Select value={form.status} onValueChange={(v) => update("status", v)}>
            <SelectTrigger className={selectTriggerClassName}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label>Baptized</Label>
          <Select value={form.baptism_status ? "yes" : "no"} onValueChange={(v) => update("baptism_status", v === "yes")}>
            <SelectTrigger className={selectTriggerClassName}><SelectValue placeholder="Select status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );

  const educationSection = (
    <div>
      {!isMobile && <h4 className="text-sm font-semibold text-foreground mb-2">Education Information</h4>}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
        <div className="space-y-2 md:col-span-3">
          <Label>Education Status</Label>
          <Select value={form.education_status} onValueChange={(v) => update("education_status", v === "_none" ? "" : v)}>
            <SelectTrigger className={selectTriggerClassName}><SelectValue placeholder="Select status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">None</SelectItem>
              {EDUCATION_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {showEducationDetails && (
          <>
            <div className="space-y-2 md:col-span-3">
              <Label>Year / Level</Label>
              <Select value={form.year_level} onValueChange={(v) => update("year_level", v === "_none" ? "" : v)}>
                <SelectTrigger className={selectTriggerClassName}><SelectValue placeholder="Select level" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">None</SelectItem>
                  {(YEAR_LEVELS_BY_STATUS[form.education_status] || []).map((l) => (
                    <SelectItem key={l} value={l}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-3">
              <Label>School Name</Label>
              <Input className={fieldClassName} value={form.school_name} onChange={(e) => update("school_name", e.target.value)} />
            </div>
            <div className="space-y-2 md:col-span-3">
              <Label>Field of Study (optional)</Label>
              <Input className={fieldClassName} value={form.field_of_study} onChange={(e) => update("field_of_study", e.target.value)} />
            </div>
          </>
        )}
        {showGraduatedDetails && (
          <>
            <div className="space-y-2 md:col-span-3">
              <Label>Degree Earned</Label>
              <Select value={form.degree_earned} onValueChange={(v) => update("degree_earned", v === "_none" ? "" : v)}>
                <SelectTrigger className={selectTriggerClassName}><SelectValue placeholder="Select degree" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">None</SelectItem>
                  {DEGREE_TYPES.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-3">
              <Label>School Name</Label>
              <Input className={fieldClassName} value={form.school_name} onChange={(e) => update("school_name", e.target.value)} />
            </div>
            <div className="space-y-2 md:col-span-3">
              <Label>Field of Study (optional)</Label>
              <Input className={fieldClassName} value={form.field_of_study} onChange={(e) => update("field_of_study", e.target.value)} />
            </div>
          </>
        )}
      </div>
    </div>
  );

  const employmentSection = showEmploymentSection ? (
    <div>
      {!isMobile && <h4 className="text-sm font-semibold text-foreground mb-2">Employment Information</h4>}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
        <div className="space-y-2 md:col-span-3">
          <Label>Employment Status</Label>
          <Select value={form.employment_status} onValueChange={(v) => update("employment_status", v === "_none" ? "" : v)}>
            <SelectTrigger className={selectTriggerClassName}><SelectValue placeholder="Select status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">None</SelectItem>
              {EMPLOYMENT_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {showEmploymentDetails && (
          <>
            <div className="space-y-2 md:col-span-3">
              <Label>Occupation</Label>
              <Input className={fieldClassName} value={form.occupation} onChange={(e) => update("occupation", e.target.value)} />
            </div>
            <div className="space-y-2 md:col-span-3">
              <Label>Industry / Work Type</Label>
              <Select value={form.industry} onValueChange={(v) => update("industry", v === "_none" ? "" : v)}>
                <SelectTrigger className={selectTriggerClassName}><SelectValue placeholder="Select industry" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">None</SelectItem>
                  {INDUSTRIES.map((i) => (
                    <SelectItem key={i} value={i}>{i}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-3">
              <Label>Employer Name (optional)</Label>
              <Input className={fieldClassName} value={form.employer_name} onChange={(e) => update("employer_name", e.target.value)} />
            </div>
          </>
        )}
      </div>
    </div>
  ) : null;

  const notesSection = (
    <div className="space-y-1">
      {!isMobile && <Label>Notes</Label>}
      {isMobile ? <Label>Notes</Label> : null}
      <Textarea className={textAreaClassName} value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={3} />
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-heading">{isEdit ? "Edit Member" : "Add New Member"}</SheetTitle>
        </SheetHeader>
        <MemberFormErrorBoundary onClose={onClose}>
          {showLimitWarning && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground">Member limit reached</p>
                <p className="text-xs text-muted-foreground mt-1">You have {memberCount} of {memberLimit} members.</p>
                <Button size="sm" variant="outline" className="mt-2 text-xs" onClick={() => { onClose(); navigate("/billing"); }}>Upgrade Plan</Button>
              </div>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-3">
          {isMobile ? (
            <Accordion type="single" collapsible defaultValue="personal" className="w-full rounded-lg border border-border/60">
              <AccordionItem value="personal" className="px-4">
                <AccordionTrigger className="py-4 text-sm font-semibold text-foreground hover:no-underline">Personal Information</AccordionTrigger>
                <AccordionContent>{personalInformationSection}</AccordionContent>
              </AccordionItem>
              <AccordionItem value="family" className="px-4">
                <AccordionTrigger className="py-4 text-sm font-semibold text-foreground hover:no-underline">Family Information</AccordionTrigger>
                <AccordionContent>{familySection}</AccordionContent>
              </AccordionItem>
              <AccordionItem value="address" className="px-4">
                <AccordionTrigger className="py-4 text-sm font-semibold text-foreground hover:no-underline">Address</AccordionTrigger>
                <AccordionContent>{addressSection}</AccordionContent>
              </AccordionItem>
              <AccordionItem value="church" className="px-4">
                <AccordionTrigger className="py-4 text-sm font-semibold text-foreground hover:no-underline">Church Information</AccordionTrigger>
                <AccordionContent>{churchInformationSection}</AccordionContent>
              </AccordionItem>
              <AccordionItem value="education" className="px-4">
                <AccordionTrigger className="py-4 text-sm font-semibold text-foreground hover:no-underline">Education Information</AccordionTrigger>
                <AccordionContent>{educationSection}</AccordionContent>
              </AccordionItem>
              {employmentSection && (
                <AccordionItem value="employment" className="px-4">
                  <AccordionTrigger className="py-4 text-sm font-semibold text-foreground hover:no-underline">Employment Information</AccordionTrigger>
                  <AccordionContent>{employmentSection}</AccordionContent>
                </AccordionItem>
              )}
              <AccordionItem value="notes" className="px-4 border-b-0">
                <AccordionTrigger className="py-4 text-sm font-semibold text-foreground hover:no-underline">Notes</AccordionTrigger>
                <AccordionContent>{notesSection}</AccordionContent>
              </AccordionItem>
            </Accordion>
          ) : (
            <>
              {personalInformationSection}
              <Separator />
              {familySection}
              <Separator />
              {addressSection}
              <Separator />
              {churchInformationSection}
              <Separator />
              {educationSection}
              {employmentSection && <><Separator />{employmentSection}</>}
              <Separator />
              {notesSection}
            </>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending || showLimitWarning}>
              {mutation.isPending ? "Saving..." : isEdit ? "Update" : "Add Member"}
            </Button>
          </div>
          </form>
        </MemberFormErrorBoundary>
      </SheetContent>
    </Sheet>
  );
}
